/**
 * AudioPreview — minimal HTML5 audio player with progress bar.
 * Shared across the Imaging Studio's Music Source tabs (library + AI gen).
 */
import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

export default function AudioPreview({ src, accent = "#4a9eff", "data-testid": testid }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Reset when src changes (clicking another track in the list)
    setPlaying(false);
    setProgress(0);
    if (ref.current) {
      ref.current.pause();
      try { ref.current.currentTime = 0; } catch { /* noop */ }
    }
  }, [src]);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) { el.play().catch(() => { /* autoplay blocked */ }); }
    else { el.pause(); }
  };

  return (
    <div className="flex items-center gap-2 w-full" data-testid={testid}>
      <audio
        ref={ref}
        src={src}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onTimeUpdate={() => {
          const el = ref.current;
          if (el && el.duration) setProgress((el.currentTime / el.duration) * 100);
        }}
      />
      <button
        type="button"
        onClick={toggle}
        className="h-7 w-7 rounded-full grid place-items-center transition-colors shrink-0"
        style={{ backgroundColor: playing ? accent : "#1e2130", color: playing ? "#0a0d14" : accent }}
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
      </button>
      <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: "#1e2130" }}>
        <div
          className="h-full transition-all"
          style={{ width: `${progress}%`, background: accent }}
        />
      </div>
    </div>
  );
}
